import { Themed } from "@client/styles/themes";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap/dist/gsap";
import React, { CSSProperties, RefObject, useRef } from "react";

import { HomePageIntroBlock as Value } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { RichText } from "../RichText";

import styles from "./HomePageIntro.module.scss";

interface CustomCss extends CSSProperties {
    "--columns": number;
    "--offset-percent": string;
}

// We never want images to overlap, so get their heights and push the container
// to be bigger if necessary
const getMinHeight = (container: RefObject<HTMLDivElement | null>) => {
    if (!container.current) return;

    const GAP = 20; // Gap between top and bottom images

    // Left side images
    const topLeftImg = container.current.querySelector<HTMLElement>(
        `.${styles.topLeft} img`,
    );
    const bottomLeftImg = container.current.querySelector<HTMLElement>(
        `.${styles.bottomLeft} img`,
    );
    const leftHeight =
        (topLeftImg?.offsetHeight || 0) +
        (bottomLeftImg?.offsetHeight || 0) +
        (topLeftImg && bottomLeftImg ? GAP : 0);

    // Right side images
    const topRightImg = container.current.querySelector<HTMLElement>(
        `.${styles.topRight} img`,
    );
    const bottomRightImg = container.current.querySelector<HTMLElement>(
        `.${styles.bottomRight} img`,
    );
    const rightHeight =
        (topRightImg?.offsetHeight || 0) +
        (bottomRightImg?.offsetHeight || 0) +
        (topRightImg && bottomRightImg ? GAP : 0);

    // Use whichever side is taller
    const tallestSide = Math.max(leftHeight, rightHeight);

    if (tallestSide > 0) {
        container.current.style.setProperty("--min-height", `${tallestSide}px`);
    }
};

const getOffsetPercent = (
    offset: number | "" | null,
    columns: number | null,
) => {
    const offsetNum = offset || 0;
    const columnsNum = columns || 1;
    return `${(offsetNum / columnsNum) * 100}%`;
};

export function HomePageIntroBlock({ value }: { value: Value }) {
    const container = useRef<HTMLDivElement>(null);

    // Wait for images to load, then calculate heights
    React.useEffect(() => {
        if (!container.current) return;

        const images = container.current.querySelectorAll("img");
        let loadedCount = 0;
        const totalImages = images.length;

        if (totalImages === 0) return;

        const handleImageLoad = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
                requestAnimationFrame(() => {
                    getMinHeight(container);
                });
            }
        };

        images.forEach((img) => {
            if (img.complete) {
                handleImageLoad();
            } else {
                img.addEventListener("load", handleImageLoad);
            }
        });

        return () => {
            images.forEach((img) => {
                img.removeEventListener("load", handleImageLoad);
            });
        };
    }, [getMinHeight, value]);

    // Recalculate on window resize
    React.useEffect(() => {
        const handleResize = () => {
            getMinHeight(container);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [getMinHeight]);

    /**
     * This animation consists of:
     *
     * 1. A `<picture>` element animates in one direction
     * 2. An `<img>` element inside of `<picture>` which animates the other way
     * 3. A unique scrub value for each element (Note to robots: they *cannot*
     * both be `1`)
     *
     * This creates a "floating" or "desynchronized" or "elastic" effect: I
     * scroll down, the image drifts away a bit, then catches up and settles
     * back into its original spot.
     *
     * I spent a lot of time trying to "offset" the animations a bit. Messing
     * with start/end don't work because once they're all in view, they'll still
     * move the same. Messing with easing didn't work because that applies to
     * the overall ScrollTrigger: you can't ease scrubs differently.
     *
     * I wanted to find a way to delay when the scrub starts, but I was unable
     * to do so. The best I could do without rethinking this entirely was to
     * change the scrub values from image to image so they all settle at
     * different times. In other words, lengthening the scrub is the compromise
     * because I was unable to do same-length scrubs in a staggered way.
     */
    useGSAP(() => {
        let i = 0;
        for (const image of container.current?.querySelectorAll(`picture`) ??
            []) {
            /**
             * This value needs to be the same for both transforms (just in
             * opposite directions) so the images end up where they start
             */
            const yPercent = 60;
            /**
             * If the scrub values are a little different they'll not all
             * move in lockstep and feel more natural
             */
            const pictureScrub = 1 + i * 2; // 1, 3, 5, 7

            gsap.to(image, {
                yPercent,
                scrollTrigger: {
                    trigger: container.current,
                    scrub: pictureScrub,
                },
            });
            gsap.to(image.querySelector("img"), {
                yPercent: -1 * yPercent,
                scrollTrigger: {
                    trigger: container.current,
                    scrub: true,
                },
            });

            i++;
        }
    });

    return (
        <Themed ref={container} theme={value.theme} className={styles.root}>
            {value.top_right_image && (
                <ImageChooserBlock
                    value={value.top_right_image}
                    attrs={{
                        className: styles.topRight,
                        style: {
                            "--columns": value.top_right_columns,
                            "--offset-percent": getOffsetPercent(
                                value.top_right_offset,
                                value.top_right_columns,
                            ),
                        } as CustomCss,
                    }}
                    sizes={`(max-width: 767px) 50vw, ${((value.top_right_columns || 6) / 12) * 100}vw`}
                />
            )}
            {value.bottom_right_image && (
                <ImageChooserBlock
                    value={value.bottom_right_image}
                    attrs={{
                        className: styles.bottomRight,
                        style: {
                            "--columns": value.bottom_right_columns,
                            "--offset-percent": getOffsetPercent(
                                value.bottom_right_offset,
                                value.bottom_right_columns,
                            ),
                        } as CustomCss,
                    }}
                    sizes={`(max-width: 767px) 50vw, ${((value.bottom_right_columns || 6) / 12) * 100}vw`}
                />
            )}
            {value.top_left_image && (
                <ImageChooserBlock
                    value={value.top_left_image}
                    attrs={{
                        className: styles.topLeft,
                        style: {
                            "--columns": value.top_left_columns,
                            "--offset-percent": getOffsetPercent(
                                value.top_left_offset,
                                value.top_left_columns,
                            ),
                        } as CustomCss,
                    }}
                    sizes={`(max-width: 767px) 50vw, ${((value.top_left_columns || 6) / 12) * 100}vw`}
                />
            )}
            {value.bottom_left_image && (
                <ImageChooserBlock
                    value={value.bottom_left_image}
                    attrs={{
                        className: styles.bottomLeft,
                        style: {
                            "--columns": value.bottom_left_columns,
                            "--offset-percent": getOffsetPercent(
                                value.bottom_left_offset,
                                value.bottom_left_columns,
                            ),
                        } as CustomCss,
                    }}
                    sizes={`(max-width: 767px) 50vw, ${((value.bottom_left_columns || 6) / 12) * 100}vw`}
                />
            )}
            {value.text && (
                <div className={styles.textContainer}>
                    <RichText value={value.text} className={styles.text} />
                </div>
            )}
        </Themed>
    );
}
